import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { en } from './en';
import { fr, type Messages } from './fr';
import { resolveInitialLocale, setActiveLocale, type AppLocale } from './locale';

/**
 * The i18n layer (redesign, D-145): i18next + react-i18next, one namespace,
 * catalogues bundled with the app (no fetch, so the first paint is translated).
 * Components call `useTranslation()`; plain functions read `currentLocale()`.
 */

declare module 'i18next' {
  interface CustomTypeOptions {
    defaultNS: 'translation';
    resources: { translation: Messages };
  }
}

const initial = resolveInitialLocale();

function apply(locale: AppLocale): void {
  setActiveLocale(locale);
  document.documentElement.lang = locale;
}

void i18n.use(initReactI18next).init({
  resources: { fr: { translation: fr }, en: { translation: en } },
  lng: initial,
  // Both catalogues carry the same keys (test/i18n.test.ts); French is the source.
  fallbackLng: 'fr',
  supportedLngs: ['fr', 'en'],
  // Catalogues are in memory: initialise synchronously so the first render is translated.
  initAsync: false,
  // React escapes text already.
  interpolation: { escapeValue: false },
  returnNull: false,
});

apply(initial);
i18n.on('languageChanged', (lng) => apply(lng === 'fr' ? 'fr' : 'en'));

/** Switch the UI language (tests, the `?lang=` override). */
export async function changeLocale(locale: AppLocale): Promise<void> {
  await i18n.changeLanguage(locale);
}

export default i18n;
